<template>
	<div class="tw-flex tw-h-full tw-flex-col tw-justify-between tw-pt-2.5">
		<div>
			<div class="tw-mb-3 tw-text-[32px] tw-font-bold">
				{{ $t('integrations.link-your-messenger') }}
			</div>
		<div class="tw-mb-3 tw-text-2xl tw-font-medium">
				{{ $t('integrations.link-your-messenger-desc') }}
			</div>

			<!-- Requirements Section -->
			<div class="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-bg-gray-100 tw-px-6 tw-py-4 tw-mb-4">
				<div class="tw-text-lg tw-font-semibold tw-text-gray-900">
					{{ $t('integrations.make-sure-you-fulfill-requirements') }}
				</div>
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
					<AppIcon name="user-01" />
					<div class="tw-text-lg tw-font-medium">
						{{ $t('integrations.you-have-access-to-verified-business-meta-account') }}
					</div>
				</div>
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
					<AppIcon name="shield-tick" class="tw-text-gray-700"/>
					<div class="tw-text-lg tw-font-medium">
						{{ $t('integrations.messenger-business-use-case-compliant') }}
						<a
							href="https://www.facebook.com/policies/commerce"
							target="_blank"
							class="tw-text-lg tw-text-blue-600"
						>{{ $t('integrations.meta-commerce-policy') }}</a>
					</div>
				</div>
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
					<AppIcon name="users" />
					<div class="tw-text-lg tw-font-medium">
						{{ $t('integrations.messenger-admin-access-to-page') }}
					</div>
				</div>
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
					<AppIcon name="message-chat-circle" />
					<div class="tw-text-lg tw-font-medium">
						{{ $t('integrations.messenger-page-messaging-enabled') }}
					</div>
				</div>
			</div>

		<!-- Messenger Embedded Business Login: starts OAuth flow -->
		<div class="tw-mb-4">
			<AppButton
				:label="$t('integrations.connect-messenger-account')"
				size="lg"
				class="facebook-btn tw-rounded-full"
				@click="startEmbeddedLogin"
				:loading="embeddedLoading"
				startIcon="facebook"
			/>
		</div>
			<hr class="tw-my-4" />
			<div class="tw-mb-2 tw-text-lg tw-font-medium tw-text-gray-500">
				{{ $t('integrations.link-manually') }}
			</div>
			<AppForm @submit="onSubmitMessenger" class="tw-mt-2">
				<div class="tw-grid tw-grid-cols-2 tw-gap-x-3">
					<div class="tw-col-span-2 lg:tw-col-span-1">
						<AppInput
							:label="$t('integrations.page-id')"
							v-model="form.page_id"
							:rules="['required']"
							class="tw-w-full"
						/>
					</div>
					<div class="tw-col-span-2 lg:tw-col-span-1">
						<AppInput
							:label="$t('integrations.page-access-token')"
							v-model="form.page_access_token"
							:rules="['required']"
							class="tw-w-full"
						/>
					</div>
					<div class="tw-col-span-2 lg:tw-col-span-1">
						<AppInput
							:label="$t('integrations.page-name')"
							v-model="form.page_name"
							class="tw-w-full"
						/>
					</div>
				</div>
				<div class="tw-mt-5 tw-flex tw-items-center tw-gap-3">
					<AppButton
						:label="$t('integrations.confirm-data')"
						size="xl"
						class="tw-rounded-full"
						type="submit"
						:loading="submitLoading"
						endIcon="arrow-narrow-right"
					/>
				</div>
			</AppForm>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Marketplace } from '../../../../entities/marketplace';
import { useRouter, useRoute } from 'vue-router';
import { api } from '@/v2/plugins/axios';
import { Alert } from '@/v2/plugins/alert';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/v2/store/shared/auth-store';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const submitLoading = ref(false);
const embeddedLoading = ref(false);
const form = ref({
	page_id: '',
	page_access_token: '',
	page_name: '',
});

const onSubmitMessenger = async () => {
	submitLoading.value = true;
	try {
		const result = await Marketplace.saveMessengerManualToken(form.value);
		if (!result) {
			return;
		}
		const authStore = useAuthStore();
		await authStore.fetchUser(true, true);
		Alert.success(t('integrations.messenger-connected-successfully'));
		// Redirect to marketplaces page after successful connection
		router.push('/v2/integrations/marketplaces');
	} finally {
		submitLoading.value = false;
	}
};

// Starts the Messenger Embedded OAuth (Business Login) by requesting a server-built URL
const startEmbeddedLogin = async () => {
	embeddedLoading.value = true;
	try {
		const resp = await api.get('/v1/messenger/embedded-oauth/authorize-url');
		const url = resp?.data?.result?.url || resp?.data?.data?.url || resp?.data?.url;
		if (url) {
			window.location.href = url;
		} else {
			Alert.error(t('integrations.messenger_errors.unknown_error'));
		}
	} catch (e) {
		Alert.error(t('integrations.messenger_errors.unknown_error'));
	} finally {
		embeddedLoading.value = false;
	}
};

// Show success/error messages coming from the OAuth callback
onMounted(async () => {
	const messengerError = route.query?.messenger_error;
	const messengerSuccess = route.query?.messenger_success;
	const messengerSelectPage = route.query?.messenger_select_page;

	if (messengerSuccess) {
		const authStore = useAuthStore();
		await authStore.fetchUser(true, true);
		Alert.success(t('integrations.messenger-connected-successfully'));
		// Redirect to marketplaces page after successful OAuth connection
		router.push('/v2/integrations/marketplaces');
	}

	if (messengerError) {
		const key = `integrations.messenger_errors.${messengerError}`;
		const msg = t(key);
		Alert.error(msg || t('integrations.messenger_errors.unknown_error'));
		// Clean the query to avoid showing the message again on refresh
		const { messenger_error, ...rest } = route.query;
		router.replace({ path: route.path, query: rest });
	}

	if (messengerSelectPage) {
		// TODO: Handle page selection if user has multiple pages
		Alert.info(t('integrations.messenger_errors.select_page_required'));
		const { messenger_select_page, ...rest } = route.query;
		router.replace({ path: route.path, query: rest });
	}
});
</script>

<style lang="scss" scoped>
.facebook-btn {
	background-color: #1877f2 !important;
	color: white !important;

	&:hover {
		background-color: #166fe5 !important;
	}

	&:active {
		background-color: #1469d6 !important;
	}
}
</style>

